/**
 * @module ChartSettingsPanel/usePanelQBSnapshot
 * @description Panel 显隐专用 QB 快照 — 仅 ChartSettingsPanel 使用
 */

import { useMemo } from 'react';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import { useChartSettingsSelector } from '../../context';
import { detectDateDimensionsInList } from '../../manifest/dimensionDateUtils';
import { useQuerySelector } from '../../hooks/deps';
import type { QBStateSnapshot } from '../../types';
import { toChartTypeEnum } from '../../utils/toChartTypeEnum';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import {
  isCrosstabActive,
  type ICrosstabConfig,
} from '../../utils/crosstab';

export function usePanelQBSnapshot(): QBStateSnapshot {
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const mode = useQuerySelector(s => s.mode as TChartCalculateMode);

  const sortType = useChartSettingsSelector(
    s => (s.base.sortBy as string) || '',
  );
  const dualYAxisEnabled = useChartSettingsSelector(
    s => !!(s.draft.dualYAxisEnabled ?? s.base.dualYAxisEnabled),
  );
  const effectiveCrosstab = useChartSettingsSelector(
    s => (s.draft.crosstab ?? s.base.crosstab) as ICrosstabConfig | undefined,
  );

  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const hasDateDimension = useMemo(
    () => detectDateDimensionsInList(columnDimensions),
    [columnDimensions],
  );
  const modeLabel = useMemo(
    (): 'aggregation' | 'raw' =>
      mode === ChartCalculateMode.RawColumn ? 'raw' : 'aggregation',
    [mode],
  );

  const isCrosstabActiveFlag = useMemo(
    () => isCrosstabActive(effectiveCrosstab, columnDimensions, columnMetrics),
    [effectiveCrosstab, columnDimensions, columnMetrics],
  );

  return useMemo(
    (): QBStateSnapshot => ({
      chartType,
      columnDimensions,
      columnMetrics,
      hasDateDimension,
      mode: modeLabel,
      sortType,
      dualYAxisEnabled,
      isCrosstabActive: isCrosstabActiveFlag,
    }),
    [
      chartType,
      columnDimensions,
      columnMetrics,
      hasDateDimension,
      modeLabel,
      sortType,
      dualYAxisEnabled,
      isCrosstabActiveFlag,
    ],
  );
}
